import { MediaImage } from '@/lib/mediaDisplay';
import { useState, useMemo } from 'react'
import { useMedia, MediaFolder } from '@wordpuppi/shared'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { mediaIcon, mediaTypeLabel } from '@/lib/mediaIcon'
import { Type } from 'lucide-react'

interface MediaBrowserProps {
  siteSlug: string
  /** Current folder context (e.g., 'posts' when editing a post) */
  currentFolder?: MediaFolder
  /** Called when user selects an item; `mimeType` lets callers insert a link
   *  instead of an <img> for documents (AB#662) */
  onSelect: (url: string, alt?: string, mimeType?: string) => void
  /** AB#662: include non-image media (documents, video, audio). Default: images only. */
  allowDocuments?: boolean
  /** Narrow the list to one media kind — fonts, for the theme font picker. */
  kind?: 'font'
  /** Called when user cancels */
  onCancel: () => void
}

export function MediaBrowser({
  siteSlug,
  currentFolder = 'posts',
  onSelect,
  onCancel,
  allowDocuments = false,
  kind,
}: MediaBrowserProps) {
  // Fonts live in the root library, never in a post/page folder — open on All.
  const [activeTab, setActiveTab] = useState<'current' | 'all'>(kind === 'font' ? 'all' : 'current')
  const [search, setSearch] = useState('')

  // Fetch media for current folder
  const { data: currentFolderMedia, isLoading: loadingCurrent } = useMedia(siteSlug, {
    folder: currentFolder,
  })

  // Fetch all media (root + all folders)
  const { data: allMedia, isLoading: loadingAll } = useMedia(siteSlug, {})

  // ponytail: client-side filter, no `kind` query param — the list endpoint is
  // already fetched whole for the grid.
  const keep = (m: { mime_type?: string | null }) =>
    kind === 'font'
      ? !!m.mime_type?.startsWith('font/')
      : allowDocuments || !!m.mime_type?.startsWith('image/')

  const currentImages = useMemo(() => {
    const items = (currentFolderMedia?.data || []).filter(keep)
    if (!search) return items
    const searchLower = search.toLowerCase()
    return items.filter(
      (m) =>
        m.filename?.toLowerCase().includes(searchLower) ||
        m.alt_text?.toLowerCase().includes(searchLower)
    )
  }, [currentFolderMedia?.data, search, allowDocuments, kind])

  const allImages = useMemo(() => {
    const items = (allMedia?.data || []).filter(keep)
    if (!search) return items
    const searchLower = search.toLowerCase()
    return items.filter(
      (m) =>
        m.filename?.toLowerCase().includes(searchLower) ||
        m.alt_text?.toLowerCase().includes(searchLower)
    )
  }, [allMedia?.data, search, allowDocuments, kind])

  const noun = kind === 'font' ? 'fonts' : allowDocuments ? 'files' : 'images'
  const folderLabel = currentFolder === 'posts' ? 'Post Media' : currentFolder === 'pages' ? 'Page Media' : 'Current Folder'

  const renderGrid = (images: typeof currentImages, loading: boolean) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-32 text-muted-foreground">
          Loading...
        </div>
      )
    }

    if (images.length === 0) {
      return (
        <div className="flex items-center justify-center h-32 text-muted-foreground">
          {search ? `No ${noun} match your search` : `No ${noun} in this folder`}
        </div>
      )
    }

    return (
      <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto p-1">
        {images.map((media) => (
          <button
            key={media.id}
            type="button"
            // ponytail: pass the ROOT-RELATIVE url — this value is inserted into
            // (and published in) content; resolving it would bake the env host
            // into pages. MediaImage resolves protected bytes for display only.
            onClick={() => onSelect(media.url, media.alt_text || media.original_name || media.filename, media.mime_type)}
            className="media-tile relative aspect-square rounded border border-border hover:border-primary hover:ring-2 hover:ring-primary/20 transition-[border-color,box-shadow] overflow-hidden group"
            title={media.original_name || media.filename}
          >
            {kind === 'font' ? (
              <span className="flex h-full w-full flex-col items-center justify-center gap-1 bg-muted p-1">
                <Type className="h-7 w-7 text-muted-foreground" strokeWidth={1.5} />
                <span className="w-full truncate text-[10px] text-muted-foreground">
                  {media.original_name || media.filename}
                </span>
              </span>
            ) : media.mime_type?.startsWith('image/') ? (
              <MediaImage
                scopeKey={siteSlug}
                src={media.url}
                alt={media.alt_text || media.filename}
                className="w-full h-full object-cover text-transparent"
                loading="lazy"
              />
            ) : (
              (() => {
                const Icon = mediaIcon(media.mime_type, media.original_name || media.filename)
                return (
                  <span className="flex h-full w-full flex-col items-center justify-center gap-1 bg-muted">
                    <Icon className="h-7 w-7 text-muted-foreground" strokeWidth={1.5} />
                    <span className="text-[10px] text-muted-foreground">
                      {mediaTypeLabel(media.mime_type, media.original_name || media.filename)}
                    </span>
                  </span>
                )
              })()
            )}
            <div className="absolute inset-x-0 bottom-0 bg-popover/90 text-popover-foreground text-xs p-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
              {media.original_name || media.filename}
            </div>
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <Input
        type="text"
        placeholder={`Search ${noun}...`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="h-8"
      />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'current' | 'all')}>
        <TabsList className="w-full">
          <TabsTrigger value="current" className="flex-1">
            {folderLabel}
          </TabsTrigger>
          <TabsTrigger value="all" className="flex-1">
            All Media
          </TabsTrigger>
        </TabsList>

        <TabsContent value="current" className="mt-2">
          {renderGrid(currentImages, loadingCurrent)}
        </TabsContent>

        <TabsContent value="all" className="mt-2">
          {renderGrid(allImages, loadingAll)}
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
